import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export interface SettingRow {
  value: string;
  value_i18n: Record<string, string> | null;
}

export interface SettingsService {
  getSettings(keys: string[]): Promise<Record<string, string>>;
  getSettingsFull(keys: string[]): Promise<Record<string, SettingRow>>;
  upsertSetting(key: string, value: string | null): Promise<void>;
  upsertSettingI18n(key: string, value_i18n: Record<string, string>): Promise<void>;
  upsertSettings(entries: Array<{ key: string; value: string | null; updatedAt?: string }>): Promise<void>;
}

export const supabaseSettingsService: SettingsService = {
  async getSettings(keys) {
    if (keys.length === 0) return {};

    const { data, error } = await supabase
      .from("site_settings")
      .select("key, value")
      .in("key", keys);

    if (error) throw error;

    return (data ?? []).reduce<Record<string, string>>((acc, row) => {
      acc[row.key] = row.value ?? "";
      return acc;
    }, {});
  },

  async getSettingsFull(keys) {
    if (keys.length === 0) return {};

    const { data, error } = await supabase
      .from("site_settings")
      .select("key, value, value_i18n")
      .in("key", keys);

    if (error) throw error;

    return (data ?? []).reduce<Record<string, SettingRow>>((acc, row) => {
      const i18n = row.value_i18n as Record<string, string> | null;
      acc[row.key] = {
        value: row.value ?? "",
        value_i18n: i18n && typeof i18n === "object" && !Array.isArray(i18n) ? i18n : null,
      };
      return acc;
    }, {});
  },

  async upsertSetting(key, value) {
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key, value }, { onConflict: "key" });

    if (error) throw error;
  },

  async upsertSettingI18n(key, value_i18n) {
    const { error } = await supabase
      .from("site_settings")
      .upsert(
        { key, value: value_i18n.hr || value_i18n.en || "", value_i18n: value_i18n as unknown as Json, updated_at: new Date().toISOString() },
        { onConflict: "key" },
      );

    if (error) throw error;
  },

  async upsertSettings(entries) {
    for (const entry of entries) {
      await this.upsertSetting(entry.key, entry.value ?? null);
    }
  },
};
